import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  Button,
  Stack,
  Paper,
  Divider,
  Avatar,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Link } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const FeatureLayout = ({
  title,
  subtitle,
  description,
  icon,
  iconGradient,
  breadcrumbs = [],
  benefits,
  stats,
  imageLeft,
  imageRight,
  imageCenter,
  children,
}) => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 10 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />}
              sx={{ mb: 3 }}
            >
              <MuiLink component={Link} to="/" sx={{ color: "text.secondary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>
                Home
              </MuiLink>
              {breadcrumbs.map((crumb, index) => (
                crumb.link ? (
                  <MuiLink
                    key={index}
                    component={Link}
                    to={crumb.link}
                    sx={{ color: "text.secondary", textDecoration: "none", "&:hover": { color: "primary.main" } }}
                  >
                    {crumb.label}
                  </MuiLink>
                ) : (
                  <Typography key={index} sx={{ color: "text.primary", fontWeight: 600 }}>
                    {crumb.label}
                  </Typography>
                )
              ))}
            </Breadcrumbs>
          )}

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 3,
                  background: iconGradient,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  color: "white",
                }}
              >
                {icon}
              </Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                  fontWeight: 800,
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "text.secondary",
                  mb: 2,
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  mb: 4,
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                }}
              >
                {description}
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AutoAwesomeIcon />}
                  sx={{
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 700,
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowForwardIcon />}
                  sx={{
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Watch Demo
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              {imageRight && (
                <Box
                  component="img"
                  src={imageRight}
                  alt={title}
                  sx={{
                    width: "100%",
                    borderRadius: 4,
                    // boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      {stats && stats.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <Typography variant="h3" fontWeight="800" color="primary.main">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Benefits Section */}
      {benefits && benefits.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 4,
              textAlign: "center",
              fontSize: { xs: "1.8rem", md: "2.5rem" },
            }}
          >
            Why Hosts Love This Feature
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    >
                      {benefit.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="700" gutterBottom>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Main Content */}
      {children}

      {/* How It Works Section */}
      {imageLeft && (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={imageLeft}
                alt="How it works"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" fontWeight="800" gutterBottom>
                How It Works
              </Typography>
              <List>
                {[
                  "Connect your calendar in one click",
                  "Set your preferences and pricing",
                  "Automated scheduling starts immediately",
                  "Get photo proof after every cleaning",
                ].map((text, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      )}

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          mt: 6,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          borderRadius: { xs: 0, md: 4 },
          mx: { xs: 0, md: 2 },
          mb: 4,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", color: "white" }}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Ready to Get Started?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Join thousands of hosts using FreshSweeper to automate their turnovers
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: "white",
                color: theme.palette.primary.main,
                borderRadius: "50px",
                px: 5,
                py: 1.5,
                fontWeight: 700,
                "&:hover": {
                  bgcolor: alpha(theme.palette.common.white, 0.9),
                  transform: "translateY(-2px)",
                },
              }}
            >
              Start Free Trial
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FeatureLayout;